"use strict";
/**
 * In-app help (Anthony's doc 3, "After sign-up: in the app", checklist F; Paul
 * 2026-10-28): the eight places the app points at "How Fomo-Fi works" once the user is
 * signed in. Word for word from the doc; both apps read these, so the wording can't
 * drift (rule 17).
 *
 * Where the doc's table and its screenshots differ only in punctuation, the screenshot
 * wins (Paul's brief): the card's title and the bubbles' titles carry no full stop,
 * because screenshots 10 and 11 show them as bold titles on a line of their own.
 *
 * Reused, not repeated: "How Fomo-Fi works" (the link closing every bubble, F13, and the
 * account-menu entry, F11) is WALKTHROUGH_NAME_COPY; "Help & support" is
 * ACCOUNT_MENU_HELP_COPY; Home's "This pay period" is STRIP_PAY_PERIOD_COPY; the card's ×
 * is named by WALKTHROUGH_CLOSE_LABEL_COPY ("Close").
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HOME_NO_PLAN_USUAL_NOUNS = exports.HOME_NO_PLAN_HELP_LINK_COPY = exports.HOME_NO_PLAN_HELP_LEAD_COPY = exports.HOME_NO_PLAN_BUTTON_COPY = exports.HOME_NO_PLAN_HEADING_COPY = exports.HOME_NO_PLAN_EDIT_COPY = exports.CHECKIN_FIRST_TIME_TAIL_COPY = exports.CHECKIN_FIRST_TIME_LINK_COPY = exports.CHECKIN_FIRST_TIME_LEAD_COPY = exports.PLAN_HOW_IT_WORKS_WALKTHROUGH_LINK_COPY = exports.HELP_BUBBLE_ORDER_BODY_COPY = exports.HELP_BUBBLE_ORDER_TITLE_COPY = exports.HELP_BUBBLE_ORDER_LABEL_COPY = exports.HELP_BUBBLE_CARRY_OVER_BODY_COPY = exports.HELP_BUBBLE_CARRY_OVER_TITLE_COPY = exports.HELP_BUBBLE_CARRY_OVER_LABEL_COPY = exports.HELP_BUBBLE_REMAINING_BODY_COPY = exports.HELP_BUBBLE_REMAINING_TITLE_COPY = exports.HELP_BUBBLE_REMAINING_LABEL_COPY = exports.PLAN_HELP_CARD_BUTTON_COPY = exports.PLAN_HELP_CARD_BODY_COPY = exports.PLAN_HELP_CARD_TITLE_COPY = exports.IN_APP_HELP_STEP = void 0;
exports.homeNoPlanWholeDollars = homeNoPlanWholeDollars;
exports.homeNoPlanUsualSentence = homeNoPlanUsualSentence;
/** Which walkthrough step each placement opens (the doc's "Opens walkthrough at"). */
exports.IN_APP_HELP_STEP = {
    planCard: 3,
    untilPayday: 4,
    buckets: 7,
    overspendOrder: 7,
    howItWorks: 1,
    checkin: 7,
    homeNoPlan: 1,
    accountMenu: 1,
};
// ── Plan, first visit (F1, F2) ─────────────────────────────────────────────────────
exports.PLAN_HELP_CARD_TITLE_COPY = "New to Fomo-Fi? See how it works";
exports.PLAN_HELP_CARD_BODY_COPY = "A 1-minute walkthrough of Plan, Home, Upcoming and Insights. It's always in Help if you want it later.";
exports.PLAN_HELP_CARD_BUTTON_COPY = "See how it works";
// ── The "i" bubbles (F3–F5, H1) ────────────────────────────────────────────────────
exports.HELP_BUBBLE_REMAINING_LABEL_COPY = "What is remaining balance?";
exports.HELP_BUBBLE_REMAINING_TITLE_COPY = "Remaining balance";
exports.HELP_BUBBLE_REMAINING_BODY_COPY = "Your current bank balance, minus the bills due before your next payday. It's the money you can give a job with buckets.";
exports.HELP_BUBBLE_CARRY_OVER_LABEL_COPY = "How does carry-over work?";
exports.HELP_BUBBLE_CARRY_OVER_TITLE_COPY = "Unspent money carries over";
exports.HELP_BUBBLE_CARRY_OVER_BODY_COPY = "Whatever is left in a bucket at the end of a pay period stays there and adds to the next one. $20 left in Groceries means $320 next period.";
exports.HELP_BUBBLE_ORDER_LABEL_COPY = "Why this order?";
exports.HELP_BUBBLE_ORDER_TITLE_COPY = "Why this order?";
exports.HELP_BUBBLE_ORDER_BODY_COPY = "If checking can't cover every bucket, the shortfall comes from the money you need least first. Groceries and Transportation are protected until last.";
// ── Plan's right rail (F6) ─────────────────────────────────────────────────────────
exports.PLAN_HOW_IT_WORKS_WALKTHROUGH_LINK_COPY = "See the full walkthrough";
// ── Payday check-in, step 1 (F7, F8) ───────────────────────────────────────────────
// "First check-in? See how payday works · about 1 minute", the middle part a link.
exports.CHECKIN_FIRST_TIME_LEAD_COPY = "First check-in?";
exports.CHECKIN_FIRST_TIME_LINK_COPY = "See how payday works";
exports.CHECKIN_FIRST_TIME_TAIL_COPY = "· about 1 minute";
// ── Home, no plan yet (F9, F10; screenshot 14) ─────────────────────────────────────
exports.HOME_NO_PLAN_EDIT_COPY = "Edit plan";
exports.HOME_NO_PLAN_HEADING_COPY = "No buckets yet";
exports.HOME_NO_PLAN_BUTTON_COPY = "See my plan";
/** "New here? See how Fomo-Fi works", the second part a link. */
exports.HOME_NO_PLAN_HELP_LEAD_COPY = "New here?";
exports.HOME_NO_PLAN_HELP_LINK_COPY = "See how Fomo-Fi works";
/** How each of Plan's first three buckets is named inside the sentence (screenshot 14:
 *  "on groceries", "on Splash Fund", "on transportation"). By the offer's key; any other
 *  key keeps the name the server sent. */
exports.HOME_NO_PLAN_USUAL_NOUNS = {
    groceries: "groceries",
    splash: "Splash Fund",
    transportation: "transportation",
};
/**
 * "$1,235": whole dollars with comma thousands, by hand rather than Intl so Hermes prints
 * what a browser prints. Rounds half up (1234.5 → $1,235); a negative reads "-$284", and
 * anything that rounds to zero is "$0", never "-$0".
 */
function homeNoPlanWholeDollars(amount) {
    if (!Number.isFinite(amount))
        return "$0";
    const whole = Math.round(Math.abs(amount));
    const digits = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${amount < 0 && whole !== 0 ? "-" : ""}$${digits}`;
}
/**
 * "You usually spend $284 on groceries, $512 on Splash Fund and $86 on transportation each
 * pay period. Make them buckets?" `parts` are Plan's stage-1 offers, in the server's
 * order; `amount` is the offer's `usual` as a number, printed here in whole dollars so
 * both apps print the same. Null when there's nothing to name.
 */
function homeNoPlanUsualSentence(parts) {
    if (parts.length === 0)
        return null;
    const items = parts.map((p) => `${homeNoPlanWholeDollars(p.amount)} on ${exports.HOME_NO_PLAN_USUAL_NOUNS[p.key] ?? p.name}`);
    const list = items.length === 1
        ? items[0]
        : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
    return `You usually spend ${list} each pay period. Make them buckets?`;
}
//# sourceMappingURL=inAppHelpCopy.js.map